import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, Mail, Building, User, MessageSquare, Loader2, CheckCircle2, Calendar, Rocket, Phone, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { InlineWidget } from 'react-calendly';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

const FORMSPREE_URL = 'https://formspree.io/f/xvzvpdwk';
const CALENDLY_URL = 'https://calendly.com/developer-arismeta/30min' || '';

const CalendlyEmbed = () => {
  if (!CALENDLY_URL) {
    return (
      <div className="p-8 sm:p-12 text-center" data-testid="calendly-placeholder">
        <div className="w-16 h-16 bg-teal-50 rounded-2xl flex items-center justify-center mx-auto mb-6 border border-teal-200">
          <Calendar className="w-8 h-8 text-teal-600" />
        </div>
        <h3 className="text-xl sm:text-2xl font-bold text-slate-900 mb-3">Demo Scheduling Coming Soon</h3>
        <p className="text-slate-600 max-w-md mx-auto mb-6 text-sm sm:text-base">
          Our calendar booking system is being set up. In the meantime, use the contact form above and we'll schedule a demo within 24 hours.
        </p>
        <div className="inline-flex items-center gap-2 bg-teal-50 border border-teal-200 rounded-full px-4 py-2 text-sm text-teal-700 font-medium">
          <Mail className="w-4 h-4" />
          info@arismate.com
        </div>
      </div>
    );
  }

  return (
    <InlineWidget
      url={CALENDLY_URL}
      styles={{ height: '700px', minWidth: '320px' }}
      pageSettings={{
        backgroundColor: 'ffffff',
        hideEventTypeDetails: false,
        hideLandingPageDetails: false,
        hideGdprBanner: false
      }}
    />
  );
};

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: '',
    inquiry_type: 'demo'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const res = await fetch(FORMSPREE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(formData),
      });
      if (!res.ok) throw new Error('Formspree error');
      setIsSubmitted(true);
      toast.success('Thank you! We\'ll be in touch soon.');
      setFormData({ name: '', email: '', company: '', message: '', inquiry_type: 'demo' });
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFDFD]" data-testid="contact-page">
      <Navbar />
      
      <main className="pt-24 sm:pt-32 pb-24">
        {/* Hero with Image */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 lg:px-24 mb-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="inline-flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-full px-4 py-1.5 mb-6">
                <Calendar className="w-4 h-4 text-emerald-600" />
                <span className="text-sm font-medium text-emerald-700">Get Started</span>
              </div>
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 mb-4 sm:mb-6">
                Let's Talk About{' '}
                <span className="gradient-text">Your Store</span>
              </h1>
              <p className="text-base sm:text-lg text-slate-600 max-w-xl leading-relaxed">
                Book a demo, request a custom AI build, or just say hello. We're here to help you increase conversions.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="relative"
            >
              <img 
                src="/assets/contact-page.jpg"
                alt="Professional team ready to help with your e-commerce needs"
                className="rounded-2xl sm:rounded-3xl shadow-xl w-full h-[220px] sm:h-[300px] object-cover border-2 border-slate-100"
                data-testid="contact-hero-image"
              />
            </motion.div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left - Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              <div className="bg-gradient-to-br from-emerald-600 to-emerald-500 rounded-3xl p-8 md:p-10 text-white h-full relative overflow-hidden">
                {/* Background Pattern */}
                <div className="absolute inset-0 opacity-10">
                  <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,white_1px,transparent_1px)] bg-[size:24px_24px]" />
                </div>

                <div className="relative">
                  <h2 className="text-2xl md:text-3xl font-bold mb-6">
                    Ready to Transform Your Store?
                  </h2>
                  <p className="text-emerald-100 text-lg leading-relaxed mb-8">
                    Whether you need a quick demo or a custom enterprise solution, we're here to help you increase conversions.
                  </p>

                  {/* Options */}
                  <div className="space-y-4 mb-8">
                    <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/10">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                          <Rocket className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <h4 className="font-semibold mb-1">Install on Shopify</h4>
                          <p className="text-emerald-100 text-sm">Get started instantly from the Shopify App Store</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/10">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                          <Calendar className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <h4 className="font-semibold mb-1">Book a Demo</h4>
                          <p className="text-emerald-100 text-sm">See Arismate in action with a personalized walkthrough</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/10">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                          <Building className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <h4 className="font-semibold mb-1">Custom AI Build</h4>
                          <p className="text-emerald-100 text-sm">Enterprise solutions tailored to your needs</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Contact Info */}
                  <div className="space-y-4 pt-6 border-t border-white/20">
                    <div className="flex items-center gap-3 text-emerald-100">
                      <Mail className="w-5 h-5" />
                      <span>info@arismate.com</span>
                    </div>
                     <div className="flex items-center gap-3 text-emerald-100">
                      <Phone className="w-5 h-5" />
                      <span>+91 88677 51005</span>
                    </div>
                    <div className="flex items-center gap-3 text-emerald-100">
                      <MapPin className="w-10 h-10" />
                      <span>Vinyas Vijay, 41, Dr. BR Ambedkar Rd, Ramanashree Enclave, Bilekahalli, Bengaluru, Karnataka 560076</span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Right - Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
            >
              {isSubmitted ? (
                <div className="bg-emerald-50 rounded-3xl p-8 md:p-10 h-full flex flex-col items-center justify-center text-center">
                  <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mb-6">
                    <CheckCircle2 className="w-10 h-10 text-emerald-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-3">Thank You!</h3>
                  <p className="text-slate-600 mb-6">We've received your request and will be in touch within 24 hours.</p>
                  <Button 
                    variant="outline" 
                    onClick={() => setIsSubmitted(false)}
                    data-testid="submit-another-btn"
                  >
                    Submit Another Request
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="bg-white rounded-3xl p-8 md:p-10 border border-slate-200 shadow-sm" data-testid="contact-form">
                  <h3 className="text-2xl font-bold text-slate-900 mb-6">Get in Touch</h3>
                  
                  <div className="space-y-5">
                    {/* Name */}
                    <div>
                      <Label htmlFor="name" className="text-slate-700 mb-2 block">Full Name</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                        <Input
                          id="name"
                          name="name"
                          type="text"
                          placeholder="John Doe"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          className="pl-10 bg-slate-50 border-slate-200 focus:border-emerald-500 focus:ring-emerald-500"
                          data-testid="contact-name-input"
                        />
                      </div>
                    </div>

                    {/* Email */}
                    <div>
                      <Label htmlFor="email" className="text-slate-700 mb-2 block">Email Address</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          placeholder="john@company.com"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="pl-10 bg-slate-50 border-slate-200 focus:border-emerald-500 focus:ring-emerald-500"
                          data-testid="contact-email-input"
                        />
                      </div>
                    </div>

                    {/* Company */}
                    <div>
                      <Label htmlFor="company" className="text-slate-700 mb-2 block">Company Name</Label>
                      <div className="relative">
                        <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                        <Input
                          id="company"
                          name="company"
                          type="text"
                          placeholder="Your Company"
                          value={formData.company}
                          onChange={handleChange}
                          required
                          className="pl-10 bg-slate-50 border-slate-200 focus:border-emerald-500 focus:ring-emerald-500"
                          data-testid="contact-company-input"
                        />
                      </div>
                    </div>

                    {/* Message */}
                    <div>
                      <Label htmlFor="message" className="text-slate-700 mb-2 block">Message</Label>
                      <div className="relative">
                        <MessageSquare className="absolute left-3 top-3 w-5 h-5 text-slate-400" />
                        <Textarea
                          id="message"
                          name="message"
                          placeholder="Tell us about your store and what you're looking for..."
                          value={formData.message}
                          onChange={handleChange}
                          required
                          rows={4}
                          className="pl-10 bg-slate-50 border-slate-200 focus:border-emerald-500 focus:ring-emerald-500 resize-none"
                          data-testid="contact-message-input"
                        />
                      </div>
                    </div>

                    {/* Submit Button */}
                    <Button 
                      type="submit" 
                      className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-6 text-base"
                      disabled={isSubmitting}
                      data-testid="contact-submit-btn"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send className="w-5 h-5 mr-2" />
                          Send Message
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              )}
            </motion.div>
          </div>
        </section>

        {/* Calendly Section */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 lg:px-24 mt-16 sm:mt-24 mb-8" data-testid="calendly-section">
          <motion.div
            className="text-center mb-8 sm:mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="inline-flex items-center gap-2 bg-teal-50 border border-teal-200 rounded-full px-4 py-1.5 mb-6">
              <Calendar className="w-4 h-4 text-teal-600" />
              <span className="text-sm font-medium text-teal-700">Book a Demo</span>
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Prefer to Talk?{' '}
              <span className="gradient-text">Schedule a Call</span>
            </h2>
            <p className="text-base sm:text-lg text-slate-600 max-w-xl mx-auto">
              Book a personalized demo with our team and see Arismate AI in action for your store.
            </p>
          </motion.div>

          <motion.div
            className="bg-white rounded-2xl sm:rounded-3xl border-2 border-teal-100 shadow-sm overflow-hidden"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <CalendlyEmbed />
          </motion.div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default ContactPage;
